import { useRef } from "react"
import * as PropTypes from "prop-types"
import classNames from "classnames"
import { Heading, Icon, Overlay } from "@oysterhr/ui"
import { Engagement } from "@oysterhr/schema"

type SidePanelProps = {
  title: string
  open: boolean | undefined
  wide: boolean | undefined
  onRequestClose: () => void
  children: JSX.Element
  noBackdrop?: boolean
  className?: string
  engagement?: Engagement
  overlay?: boolean
}

const SidePanel = ({
  title,
  open,
  onRequestClose,
  wide,
  children,
  noBackdrop,
  className,
  overlay = false,
}: SidePanelProps) => {
  const closeCaptureElement = useRef(null)
  return (
    <>
      {noBackdrop ? null : (
        <div
          ref={closeCaptureElement}
          data-testid="side-panel-backdrop"
          className={classNames(
            "inset-0 z-10 cursor-default",
            {
              fixed: open,
              hidden: !open,
            },
            className,
          )}
          onClick={e => {
            if (e.target === closeCaptureElement.current) {
              onRequestClose()
            }
          }}
        ></div>
      )}
      <div
        style={{ boxShadow: "-1px 0px 0px #F2F2F2" }}
        className={classNames(
          "fixed bottom-0 right-0 top-0 z-20 h-screen transform border-gray-300 bg-white p-4 transition-all duration-150 ease-out",
          {
            "w-72": !wide,
            "w-96": wide,
            "translate-x-0": open,
            "translate-x-full": !open,
          },
          className,
        )}
        data-testid="side-panel"
      >
        <div className="flex h-full flex-col">
          <div className="mb-2 flex justify-between">
            <Heading as="h2" size="2xl" className="my-2">
              {title}
            </Heading>
            <button data-testid="side-panel-close-button" onClick={() => onRequestClose()}>
              <Icon name="Times" size="sm" />
            </button>
          </div>
          {children}
        </div>
      </div>
      {overlay && open && (
        <div onClick={() => onRequestClose()}>
          <Overlay className="absolute z-10" />
        </div>
      )}
    </>
  )
}

SidePanel.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  children: PropTypes.any,
}

export default SidePanel
